import type { NodeDef } from './types';
import { BALANCE } from '../data/balance';
import { ENEMIES } from '../data/enemies';
import { NODE_BY_ID } from '../data/nodes';
import { DEFAULT_BALANCE, type ResolvedBalance } from './balance';

/**
 * Formules économiques du MVP.
 * Tout est arrondi à l'entier : l'or est une monnaie entière.
 */

/** Vague globale W = (L − 1) × 6 + D (D = 6 pour l'Invasion). */
export function waveOf(level: number, day: number): number {
  return (level - 1) * BALANCE.wavesPerLevel + day;
}

/**
 * Décompose une vague globale W en (niveau d'expédition L, jour D).
 * Inverse exact de `waveOf` : `levelDayOf(waveOf(L, D)) === { level: L, day: D }`
 * pour D ∈ [1, 6] (6 = Invasion).
 */
export function levelDayOf(wave: number): { level: number; day: number } {
  const w = Math.max(1, Math.round(wave));
  const level = Math.floor((w - 1) / BALANCE.wavesPerLevel) + 1;
  return { level, day: w - (level - 1) * BALANCE.wavesPerLevel };
}

/**
 * PV d'un ennemi à la vague W (`bal` : surcharges de debug éventuelles).
 *
 * COMMENTS v0.3.0 §3.2 / réponse 18 — **deux** multiplicateurs :
 * `PV = base × hpDayGrowth^(D − 1) × hpLevelCurve[L − 1]`, où D est le jour
 * dans le niveau (1…5, Invasion = 6) et L le niveau d'expédition. La croissance
 * par jour est petite (1,12) devant celle par niveau : monter d'un niveau est
 * la vraie barrière, pas tenir cinq jours de plus.
 */
export function enemyHp(baseHp: number, wave: number, bal: ResolvedBalance = DEFAULT_BALANCE): number {
  const { level, day } = levelDayOf(wave);
  // v0.5.1 (§D.17) : la croissance par niveau est une **table** ; au-delà de la
  // table (et quand une surcharge de debug la neutralise) on prolonge en
  // géométrique avec `hpLevelGrowth`.
  const curve = bal.hpLevelCurve;
  const levelMult =
    curve.length === 0
      ? Math.pow(bal.hpLevelGrowth, level - 1)
      : level <= curve.length
        ? curve[level - 1]
        : curve[curve.length - 1] * Math.pow(bal.hpLevelGrowth, level - curve.length);
  const scale = Math.pow(bal.hpDayGrowth, day - 1) * levelMult;
  return Math.max(1, Math.round(baseHp * scale * bal.enemyHpMult));
}

/**
 * Or de base d'un ennemi à la vague W (avant multiplicateurs du joueur).
 *
 * COMMENTS v0.6.0 §1.4 / arbitrage **A24** : **deux** croissances, comme pour
 * les PV — `or = base × goldGrowth^(W−1) × goldLevelGrowth^(L−1)`, où W est la
 * vague globale et L le niveau d'Expédition. `goldLevelGrowth` vaut 1 (neutre)
 * tant que la passe d'équilibrage ne l'a pas réglée.
 */
export function enemyGold(
  baseGold: number,
  wave: number,
  bal: ResolvedBalance = DEFAULT_BALANCE,
): number {
  const { level } = levelDayOf(wave);
  const scale =
    Math.pow(bal.goldGrowth, wave - 1) * Math.pow(bal.goldLevelGrowth, level - 1);
  return Math.max(1, Math.round(baseGold * scale * bal.enemyGoldMult));
}

/**
 * Contexte de prix d'un nœud de l'**Abîme** (A94-1) : le prix d'un niveau est
 * `3 × Exp(porte)`, et `Exp` dépend du multiplicateur d'or de l'arbre
 * (*Butin* + *Larcin*). `bestLevel` n'entre pas dans le prix (il ne sert qu'à
 * la porte, `core/skilltree.ts : canBuyNode`) ; il est accepté ici pour que le
 * même contexte serve aux deux. Sans contexte, `goldMult` vaut 1.
 */
export interface NodeCostContext {
  bestLevel?: number;
  goldMult?: number;
}

/**
 * Coût du prochain niveau d'un nœud :
 * `coûtBase × nodeCostMult × nodeCostGrowth^(niveau actuel)`.
 *
 * `bal` porte les surcharges d'équilibrage (phase 6 : `nodeCostGrowth`,
 * `nodeCostMult`) ; par défaut `nodeCostMult` vaut 1 et `nodeCostGrowth`
 * celui de `BALANCE`, ce qui rend le même nombre qu'avant au bit près.
 *
 * v0.46.0 (A94-1) : un nœud de l'**Abîme** (`gate`) a son propre prix,
 * `abyssNodeCost` — les trois autres branches sont inchangées au bit près.
 */
export function nodeCost(
  nodeId: string,
  currentLevel: number,
  bal: ResolvedBalance = DEFAULT_BALANCE,
  ctx: NodeCostContext = {},
): number {
  const def = NODE_BY_ID[nodeId];
  if (!def) return Infinity;
  if (def.gate) return abyssNodeCost(nodeId, currentLevel, ctx.goldMult ?? 1, bal);
  return Math.round(def.baseCost * bal.nodeCostMult * Math.pow(bal.nodeCostGrowth, currentLevel));
}

/** Coût total investi dans un nœud du niveau 0 au niveau `level`. */
export function nodeInvested(
  nodeId: string,
  level: number,
  bal: ResolvedBalance = DEFAULT_BALANCE,
  ctx: NodeCostContext = {},
): number {
  let total = 0;
  for (let i = 0; i < level; i++) total += nodeCost(nodeId, i, bal, ctx);
  return total;
}

// ─────────────────────────────────────────── Mode infini : Exp(N) (A94)

/**
 * Or de base **moyen** d'un ennemi ordinaire : moyenne arithmétique de
 * `baseGold` sur tous les ennemis non-boss de `data/enemies.ts` — calculée,
 * jamais saisie (A94, révision §2).
 */
export const AVERAGE_ENEMY_BASE_GOLD: number = (() => {
  const pool = ENEMIES.filter((e) => !e.boss);
  return pool.reduce((sum, e) => sum + e.baseGold, 0) / Math.max(1, pool.length);
})();

/**
 * **Exp(N)** — « une Expédition du niveau N, en or » (révision du GD du 15/10,
 * §2 ; A94-1, A94-13) :
 *
 * `Exp(N) = 8 × orDeBaseMoyen × goldMult × goldGrowth^(W(N, 3) − 1)
 *           × goldLevelGrowth^(N − 1) × enemyGoldMult`
 *
 * où `W(N, 3)` est la vague du jour 3, milieu du niveau, et `goldMult` le
 * multiplicateur d'or de l'arbre (*Butin* + *Larcin*, `DerivedStats.goldMult`).
 * Arrondi à l'entier, au moins 1. Unité de prix de toute la branche de
 * l'Abîme, de la Forge et des *Corrupted GachaDice*.
 */
export function expeditionGoldRef(
  level: number,
  goldMult: number,
  bal: ResolvedBalance = DEFAULT_BALANCE,
): number {
  const n = Math.max(1, Math.floor(level));
  const wave = waveOf(n, 3);
  const value =
    BALANCE.expeditionKillsRef *
    AVERAGE_ENEMY_BASE_GOLD *
    goldMult *
    Math.pow(bal.goldGrowth, wave - 1) *
    Math.pow(bal.goldLevelGrowth, n - 1) *
    bal.enemyGoldMult;
  return Math.max(1, Math.round(value));
}

/**
 * Multiplicateur d'or de l'arbre (*Butin* + *Larcin*, additifs — §D.18) :
 * c'est `DerivedStats.goldMult`, lu ici sans passer par `getDerivedStats`
 * (prix des nœuds de l'Abîme dans `canBuyNode`).
 */
export function goldMultOf(levels: Readonly<Record<string, number>>): number {
  return (
    1 +
    BALANCE.nodeGoldPerLevel * (levels.shop_gold ?? 0) +
    BALANCE.nodeLarcinGoldPerLevel * (levels.shop_larcin ?? 0)
  );
}

/**
 * **Porte** du niveau `owned + 1` d'un nœud de l'Abîme (A94-3) :
 * `gate.level + gate.step × owned` (`owned` = niveau déjà possédé). *Sans
 * fond* (`step` 0) : 29 pour tous ses niveaux (A94-12). `null` hors Abîme.
 */
export function abyssGateLevel(def: NodeDef | undefined, owned: number): number | null {
  if (!def?.gate) return null;
  return def.gate.level + def.gate.step * Math.max(0, Math.floor(owned));
}

/**
 * Prix du niveau `owned + 1` d'un nœud de l'Abîme (A94-1, A94-12) :
 * `round(3 × Exp(porte))` avec le `goldMult` courant ; *Sans fond* :
 * `round(3 × Exp(29) × 2^owned)`. `Infinity` hors Abîme.
 */
export function abyssNodeCost(
  nodeId: string,
  owned: number,
  goldMult: number,
  bal: ResolvedBalance = DEFAULT_BALANCE,
): number {
  const def = NODE_BY_ID[nodeId];
  const gate = abyssGateLevel(def, owned);
  if (!def || gate === null) return Infinity;
  const expeditions = def.costExpeditions ?? BALANCE.abyssNodeCostExpeditions;
  const growth = def.repeatable ? Math.pow(BALANCE.bottomlessCostGrowth, Math.max(0, owned)) : 1;
  return Math.round(expeditions * expeditionGoldRef(gate, goldMult, bal) * growth);
}

/**
 * **Exp(N + pas) / Exp(N)** — rapport entre deux Expéditions de référence
 * séparées de `pas` niveaux : `goldGrowth^(6 × pas) × goldLevelGrowth^pas`,
 * indépendant de N et du multiplicateur d'or de l'arbre. A servi à l'escalade
 * des recettes de la Forge (A101) ; depuis la v0.52.0 (A102) elles croissent
 * de `BALANCE.forgeGoldGrowth` par usage et cette fonction n'a plus d'appelant
 * dans le jeu (gardée, pure et testée).
 */
export function expeditionGoldGrowth(steps: number, bal: ResolvedBalance = DEFAULT_BALANCE): number {
  const n = Math.max(0, Math.floor(steps));
  return Math.pow(bal.goldGrowth, BALANCE.wavesPerLevel * n) * Math.pow(bal.goldLevelGrowth, n);
}

/**
 * Prix du **prochain** dé d'une *Corrupted GachaDice* (A94-13, A101) :
 * `max(1, round(100 000 × 1,001^achats × (1 − 0,05 × remises)))`, `achats` =
 * dés déjà achetés à cette machine depuis la dernière mort (compteur remis à
 * zéro par `acknowledgeRunLost`), `remises` = niveaux de *Négociateur* +
 * *Carte de fidélité*. Le prix ne dépend plus du niveau d'Expédition ni de
 * l'or de l'arbre (COMMENTS v0.50.0 §1.2 ; 1 Exp(niveau courant) avant la
 * v0.51.0).
 */
export function corruptedUnitCost(purchases: number, discountLevel: number): number {
  const mult = Math.max(0, 1 - BALANCE.nodeGachaDiscountPerLevel * Math.max(0, discountLevel));
  const price = BALANCE.corruptedBaseCost * Math.pow(BALANCE.corruptedCostGrowth, Math.max(0, purchases)) * mult;
  return Math.max(BALANCE.gachaMinCost, Math.round(price));
}

/**
 * Prix d'un lot de `qty` dés à une *Corrupted GachaDice* : **somme** des `qty`
 * prix unitaires successifs, le compteur avançant d'un par dé (A94-13).
 */
export function corruptedLotCost(purchases: number, qty: number, discountLevel: number): number {
  let total = 0;
  for (let i = 0; i < qty; i++) total += corruptedUnitCost(purchases + i, discountLevel);
  return total;
}

/**
 * Prix unitaire d'un dé au Gachapon, remises incluses. `discountLevel` est la
 * **somme** des niveaux de *Négociateur* et de *Carte de fidélité*, additifs
 * (A72-10) : `prix × (1 − 0,05 × niveaux)`, soit −50 % aux deux nœuds pleins,
 * plancher `gachaMinCost` (1 or).
 */
export function gachaUnitCost(baseCost: number, discountLevel: number): number {
  const mult = Math.max(0, 1 - BALANCE.nodeGachaDiscountPerLevel * Math.max(0, discountLevel));
  return Math.max(BALANCE.gachaMinCost, Math.round(baseCost * mult));
}
